/**
 * Filters for programs.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { PROGRAM_STATUS_CONFIG, PROGRAM_TYPE_CONFIG } from './program-configs';
import type { ProgramFilters, ProgramType } from './program-configs';

/** Props for {@link ProgramFiltersContent}. */
interface ProgramFiltersContentProps {
  filters: ProgramFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleStatus: (status: string) => void;
  onToggleType: (type: ProgramType) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Toggle chips for program status. */
const StatusFilter: React.FC<{
  filters: ProgramFilters;
  onToggleStatus: (status: string) => void;
}> = ({ filters, onToggleStatus }) => (
  <div className="filter-group">
    <span className="filter-group-label">Statut</span>
    <div className="filter-options">
      {Object.entries(PROGRAM_STATUS_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const isActive = filters.statuses.includes(key);
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={
              {
                '--filter-color': config.badgeColor ?? 'rgb(var(--status-success))',
              } as React.CSSProperties
            }
            onClick={() => onToggleStatus(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Toggle chips for program type. */
const TypeFilter: React.FC<{
  filters: ProgramFilters;
  onToggleType: (type: ProgramType) => void;
}> = ({ filters, onToggleType }) => (
  <div className="filter-group">
    <span className="filter-group-label">Type</span>
    <div className="filter-options">
      {(
        Object.entries(PROGRAM_TYPE_CONFIG) as Array<
          [ProgramType, (typeof PROGRAM_TYPE_CONFIG)[ProgramType]]
        >
      ).map(([value, config]) => {
        const Icon = config.icon;
        const isActive = filters.types.includes(value);
        return (
          <Button
            key={value}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              isActive ? 'filter-option-secondary--active' : 'filter-option-secondary',
            )}
            onClick={() => onToggleType(value)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Ribbon of program filters; each group is gated by RBAC + configure-panel visibility. */
export const ProgramFiltersContent: React.FC<ProgramFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onToggleStatus,
  onToggleType,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-status') && <StatusFilter filters={filters} onToggleStatus={onToggleStatus} />}
      {show('filter-type') && <TypeFilter filters={filters} onToggleType={onToggleType} />}
    </div>
  );
};
